import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Dropdown, Spin } from "antd";
import { BellOutlined, EnvironmentOutlined, IdcardOutlined, OrderedListOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { NOTIFICATIONS } from "../config/lang";
import { call } from "../util/action";
import { translate } from "../util/util";

/**
 * Icon and tone per signal kind, reusing the search panel's per-entity palette so the same
 * entity looks the same everywhere.
 */
const KIND = {
  visit: { Icon: EnvironmentOutlined, tone: "visit" },
  merchandiser: { Icon: IdcardOutlined, tone: "merchandiser" },
  survey: { Icon: OrderedListOutlined, tone: "survey" },
};

/**
 * "Seen" is per-device, and storing it server-side would need a model plus a writer, so it lives
 * here: a signature of the current signals. Anything not in the stored signature is unread.
 */
const SEEN_KEY = "picsell.notificationsSeen";

const signatureOf = items => items.map(i => `${i.key}:${i.count}`).join("|");

const readSeen = () => {
  try {
    return localStorage.getItem(SEEN_KEY) || "";
  } catch {
    return "";
  }
};

export default function AppNotifications() {
  const me = useSelector(state => state.me);
  const lang = me?.language;
  const history = useHistory();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seen, setSeen] = useState(readSeen);
  const [open, setOpen] = useState(false);

  const t = useCallback(key => translate(NOTIFICATIONS, key, lang), [lang]);

  useEffect(() => {
    let alive = true;
    call({ method: "GET", url: "stats/notifications/" }, r => {
      if (!alive) return;
      setItems(Array.isArray(r?.items) ? r.items : []);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const signature = useMemo(() => signatureOf(items), [items]);
  /* Unread while the current set of signals differs from what was last acknowledged. */
  const unread = items.length > 0 && signature !== seen;

  const markAllRead = useCallback(() => {
    try {
      localStorage.setItem(SEEN_KEY, signature);
    } catch {
      /* private mode — it will read as unread again next load */
    }
    setSeen(signature);
  }, [signature]);

  const label = item => {
    const text = t(`notif_${item.key}`);
    return item.days != null ? (text || "").replace("{n}", item.days) : text;
  };

  const go = item => {
    setOpen(false);
    markAllRead();
    history.push(item.url);
  };

  const panel = (
    <div className="shell-notif">
      <div className="shell-notif__head">
        <span className="shell-notif__title">{t("notifications")}</span>
        {unread ? (
          <button className="shell-notif__action" type="button" onClick={markAllRead}>
            {t("mark_all_read")}
          </button>
        ) : null}
      </div>

      <div className="shell-notif__list">
        {loading ? (
          <p className="shell-notif__empty">
            <Spin size="small" />
          </p>
        ) : !items.length ? (
          <p className="shell-notif__empty">{t("notif_empty")}</p>
        ) : (
          items.map(item => {
            const meta = KIND[item.kind] || KIND.visit;
            return (
              <button
                key={item.key}
                className={`shell-notif__item shell-notif__item--${meta.tone}${unread ? " is-unread" : ""}`}
                type="button"
                onClick={() => go(item)}>
                <span className="shell-notif__icon">
                  <meta.Icon />
                </span>
                <span className="shell-notif__body">
                  <span className="shell-notif__text">{label(item)}</span>
                </span>
                {/* The count is its own element on purpose — never inline with a noun, so no
                    string has to agree with a number (spec/04). */}
                <span className="shell-notif__count">{item.count}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={["click"]}
      placement="bottomRight"
      classNames={{ root: "shell-notifmenu" }}
      popupRender={() => panel}
      menu={{ items: [] }}>
      <button className="shell-iconbtn" type="button" aria-label={t("notifications")}>
        <Badge dot={unread} offset={[-2, 2]}>
          <BellOutlined />
        </Badge>
      </button>
    </Dropdown>
  );
}
